import { useMemo } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import { useAsyncTasks } from "@/modern/components/async-tasks/hooks/useAsyncTasks";
import {
  deriveBoardContextPhase,
  type BoardContextTaskPhase,
} from "@/modern/components/async-tasks/utils";

// ── Types ───────────────────────────────────────────────────────────────────

export type ContextViewStep =
  | "setup"
  | "page-picker"
  | "analysing"
  | "questions"
  | "building"
  | "suggesting"
  | "context-view";

export type TaskPhase = BoardContextTaskPhase;

// ── Pure resolver ───────────────────────────────────────────────────────────

function hasDraftQuestions(record: BoardContext): boolean {
  return (
    record.draftQuestions !== null &&
    Array.isArray(record.draftQuestions?.questions) &&
    (record.draftQuestions.questions.length ?? 0) > 0
  );
}

/**
 * Single pure function that determines which view/step to display.
 * No refs, no snapshots, no side effects.
 */
export function resolveContextViewStep(
  record: BoardContext | null,
  taskPhase: TaskPhase,
  hasNonPreviewGroups: boolean
): ContextViewStep {
  if (!record) return "setup";

  if (taskPhase === "suggesting") return "suggesting";
  if (taskPhase === "building") return "building";
  if (taskPhase === "analysing") return "analysing";

  if (record.status === "completed" && hasNonPreviewGroups) return "context-view";
  if (record.status === "completed" && !hasNonPreviewGroups) return "building";

  if (hasDraftQuestions(record)) return "questions";
  if ((record.selectedUrls ?? []).length > 0) return "page-picker";
  return "setup";
}

// ── Store hooks ─────────────────────────────────────────────────────────────

export function useBoardContextRecord(contextId: number | null): BoardContext | null {
  const { project } = useSwivuBryntumContext();
  const { version } = useSwivuStore({ storeId: STORE_KEYS.boardContextStore });

  return useMemo(() => {
    if (!project || contextId == null) return null;
    void version;
    const store = project.getCrudStore(STORE_KEYS.boardContextStore);
    if (!store) return null;
    return (store.getById(contextId) as unknown as BoardContext) ?? null;
  }, [project, contextId, version]);
}

export function useHasNonPreviewGroups(contextId: number | null): boolean {
  const { project } = useSwivuBryntumContext();
  const { version } = useSwivuStore({ storeId: STORE_KEYS.boardContextGroupStore });

  return useMemo(() => {
    if (!project || contextId == null) return false;
    void version;
    const store = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
    if (!store) return false;
    return (store.records as unknown as BoardContextGroup[]).some(
      (g) => g.boardContext === contextId && !g.isPreview
    );
  }, [project, contextId, version]);
}

// ── Main hook ───────────────────────────────────────────────────────────────

/**
 * Returns the current view step for a context.
 * null means "not ready yet" (show skeleton).
 */
export function useContextViewStep(contextId: number | null): ContextViewStep | null {
  const { isReady, tasks } = useAsyncTasks();
  const { selectedBoard } = useSwivuBryntumContext();
  const boardId = selectedBoard?.id;
  const record = useBoardContextRecord(contextId);
  const hasGroups = useHasNonPreviewGroups(contextId);

  return useMemo(() => {
    if (!isReady || contextId == null || boardId == null) return null;
    const phase = deriveBoardContextPhase(tasks, boardId, contextId);
    return resolveContextViewStep(record, phase, hasGroups);
  }, [isReady, contextId, boardId, record, hasGroups, tasks]);
}
